import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatTimeStr } from '../../../utils/storeStatusUtils';

const DAY_LABELS = {
  sunday: 'Sunday',
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
};

/**
 * StoreStatusSheet
 *
 * A premium bottom sheet that shows:
 * - Restaurant name, logo, description
 * - Live operational status badge
 * - 7-day weekly schedule (today's row highlighted)
 * - "Got it" close button
 *
 * Props:
 *   isOpen       {boolean}
 *   onClose      {() => void}
 *   restaurant   {object}  - details from RestaurantContext
 *   theme        {object}  - { primary, secondary }
 *   storeStatus  {object}  - result of computeStoreStatus()
 */
const StoreStatusSheet = ({ isOpen, onClose, theme, storeStatus }) => {
  const brandColor = theme?.secondary || '#222';
  const brandText = theme?.primary || '#fff';

  const { isOpen: open, label, sublabel, weekSchedule = [] } = storeStatus || {};

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
          />

          {/* Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 220 }}
            className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-[110] shadow-2xl flex flex-col max-h-[88vh]"
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-10 h-1 bg-gray-200 rounded-full" />
            </div>

            {/* Scrollable body */}
            <div className="overflow-y-auto flex-1 px-5 pb-6 [&::-webkit-scrollbar]:hidden">

              {/* Current status callout */}
              <div
                className="mt-4 rounded-2xl px-4 py-3 flex items-center gap-3"
                style={{ backgroundColor: open ? '#f0fdf4' : '#fff1f2' }}
              >
                <div
                  className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${open ? 'bg-green-500' : 'bg-red-500 animate-pulse'}`}
                />
                <div className="flex flex-col">
                  <span
                    className="text-sm font-black leading-tight"
                    style={{ color: open ? '#16a34a' : '#dc2626' }}
                  >
                    {label}
                  </span>
                  {sublabel && (
                    <span className="text-[11px] mt-0.5" style={{ color: open ? '#4ade80' : '#f87171' }}>
                      {sublabel}
                    </span>
                  )}
                </div>
              </div>

              {/* Weekly schedule */}
              <div className="mt-5">
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-3">
                  Weekly Hours
                </p>

                <div className="flex flex-col gap-1">
                  {weekSchedule.map((row) => (
                    <div
                      key={row.day}
                      className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl transition-colors ${
                        row.isToday ? 'border' : 'border border-transparent'
                      }`}
                      style={
                        row.isToday
                          ? { backgroundColor: `${brandColor}10`, borderColor: `${brandColor}25` }
                          : {}
                      }
                    >
                      {/* Day label */}
                      <div className="flex items-center gap-2">
                        {row.isToday && (
                          <div
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: brandColor }}
                          />
                        )}
                        <span
                          className={`text-sm ${row.isToday ? 'font-black' : 'font-medium'} text-gray-800`}
                        >
                          {DAY_LABELS[row.day] || row.day}
                        </span>
                        {row.isToday && (
                          <span
                            className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full"
                            style={{ backgroundColor: `${brandColor}20`, color: brandColor }}
                          >
                            Today
                          </span>
                        )}
                      </div>

                      {/* Time range */}
                      <span
                        className={`text-sm font-bold tabular-nums ${
                          row.isClosed ? 'text-gray-300' : row.isToday ? '' : 'text-gray-500'
                        }`}
                        style={row.isToday && !row.isClosed ? { color: brandColor } : {}}
                      >
                        {row.isClosed
                          ? 'Closed'
                          : row.is24hrs
                          ? '24 Hours'
                          : `${formatTimeStr(row.openTime)} – ${formatTimeStr(row.closeTime)}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer CTA */}
            <div className="px-5 pb-8 pt-3 flex-shrink-0 border-t border-gray-50">
              <button
                onClick={onClose}
                className="w-full py-4 rounded-2xl text-base font-black transition-all active:scale-95 shadow-md"
                style={{ backgroundColor: brandColor, color: brandText }}
              >
                Got it
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default StoreStatusSheet;
